import request, { Response, SuperAgentRequest } from 'superagent';
import { notification } from 'antd';
import { getApps, getApp } from 'firebase/app';
import { getMessaging, deleteToken } from 'firebase/messaging';
import {
  API_URL,
} from '../utils/constants';
import {
  getAccessToken,
  removeUserFromStorage,
} from '../utils/users';

import {
  SessionExpiredException, RequestTerminatedError, ModelTerminatedError,
} from '../utils/errors';

export type ResponseWrapper = Response

export type RequestWrapper = SuperAgentRequest

interface RequestHeader {
  [key: string]: string,
}

interface RequestParam {
  [key: string]: any,
}

interface GenericRequest {
  url: string,
  query?: RequestParam,
  params?: RequestParam,
  headers?: RequestHeader,
  noAuth?: boolean,
  responseType?: string,
  clientOptions?: {
    returnAll?: boolean,
  },
  frontConfigs?: {
    navigateFunction?: (route: string) => void,
  },
  timeout?: {
    response?: number,
    deadline?: number,
  },
}

export interface SuperAgentError extends Error {
  response: ResponseWrapper,
}

export const isSuperAgentError = (error: unknown): error is SuperAgentError => {
  return (error instanceof Error) && 'response' in error;
};

const preHandleKnownErrors = (error: unknown): void => {
  switch (true) {
    case (isSuperAgentError(error) && error?.response?.body?.code === -4001): {
      // You must be authenticated to perform this operation
      // const newError = error as SuperAgentError;
      removeUserFromStorage();
      window.location.href = '/';
      // throw new SessionExpiredException(newError.message);
      return;
    }

    case (isSuperAgentError(error) && error.message.includes('Request has been terminated')): {
      const newError = error as SuperAgentError;
      notification.error({
        message: 'Error',
        description: 'Requisiçao encerrada ou servidor inacessível, tente novamente mais tarde por favor.',
      });
      throw new RequestTerminatedError(newError.message);
    }

    case (isSuperAgentError(error) && error?.response?.body?.code === -15000): {
      // Equipament model name already in use
      const newError = error as SuperAgentError;
      notification.error({
        message: 'Error',
        description: 'Um equipamento com este nome já existe.',
      });
      throw new ModelTerminatedError(newError.message);
    }

    default:
      break;
  }
};

export const tratarSessaoExpirada = (args: {
  response?: ResponseWrapper,
  frontConfigs: {
    navigateFunction?: (route: string) => void,
  } | undefined
}): boolean => {
  switch (true) {
    case (
      args.response?.statusCode === 401
      || args.response?.body?.code === -4001
      || args.response?.body?.code === -1003
    ):

      if (
        args.frontConfigs
        && 'navigateFunction' in args.frontConfigs
        && args.frontConfigs.navigateFunction
      ) {
        if (getAccessToken()) {
          // Não mostra a mensagem caso o token ja tenha sido removido do storage
          notification.warning({
            message: 'Atenção',
            description: 'Login expirado. Por favor, entre novamente.',
          });
        }

        removeUserFromStorage();
        args.frontConfigs.navigateFunction('/login');
      } else {
        window.location.href = '/?session=expired';
        console.log('sem acesso ao hook navigate ou history API, usuario nao sera redirecionado');
      }
      return true;

    default:
      return false;
  }
};

export const tratarSessaoExpiradaFromError = (args: {
  error?: unknown,
  frontConfigs: {
    navigateFunction?: (route: string) => void,
  } | undefined
}): boolean => {
  const newError = args.error as SuperAgentError;
  return tratarSessaoExpirada({
    response: newError.response,
    frontConfigs: args.frontConfigs,
  });
};

export const sampleGET = async ({
  url = 'https://get.geojs.io/v1/ip/country.json?ip=8.8.8.8',
}:{
  url: string
}): Promise<ResponseWrapper> => {
  let response;

  try {
    response = await request
      .get(url);

    return response;
  } catch (error: unknown) {
    console.log(error);

    throw error;
  }
};

export const get = (args: GenericRequest): RequestWrapper => {
  const finalHeaders = args.headers || {};

  if (!args.noAuth) {
    finalHeaders['Authorization'] = `Bearer ${getAccessToken()}`;
  }
 console.log('API_URL ' + API_URL);

  return request.get(API_URL + args.url)
    .query(args.query || {})
    .set(finalHeaders || undefined)
    .responseType(args.responseType || '')
    .send(args.params || undefined)
    .on('error', (error) => {
      tratarSessaoExpiradaFromError({ frontConfigs: args.frontConfigs, error });
      preHandleKnownErrors(error);
    })
    .on('response', (response) => {
      tratarSessaoExpirada({ frontConfigs: args.frontConfigs, response });
    });
};

export const extGet = (args: GenericRequest): RequestWrapper => {
  const finalHeaders = args.headers || {};

  return request.get(args.url)
    .query(args.query || {})
    .set(finalHeaders || undefined)
    .responseType(args.responseType || '')
    .send(args.params || undefined);
};

export const extPost = async (args: GenericRequest): Promise<any> => {
  try {
    const finalHeaders = args.headers || {};
    const res = await request
      .post(args.url)
      .send(args.params || undefined)
      .query(args.query || {})
      .set(finalHeaders)
      .responseType(args.responseType || '');
    return res;
  } catch (err) {
    console.error('Erro em extPost:', err);
    throw err; // repropaga o erro para quem chamou
  }
};


export const extPut = (args: GenericRequest): RequestWrapper => {
  const finalHeaders = args.headers || {};

  return request
    .put(args.url)
    .query(args.query || {})
    .send(args.params || undefined)
    .set(finalHeaders || undefined)
    .responseType(args.responseType || '');
};

export const extPatch = (args: GenericRequest): RequestWrapper => {
  const finalHeaders = args.headers || {};

  return request
    .patch(args.url)
    .send(args.params || undefined)
    .query(args.query || {})
    .set(finalHeaders || undefined)
    .responseType(args.responseType || '');
};

export const extDel = (args: GenericRequest): RequestWrapper => {
  const finalHeaders = args.headers || {};

  return request
    .delete(args.url)
    .send(args.params || undefined)
    .query(args.query || {})
    .set(finalHeaders || undefined)
    .responseType(args.responseType || '');
};

export const post = (args: GenericRequest): RequestWrapper => {
  const finalHeaders = args.headers || {};

  if (!args.noAuth) {
    finalHeaders['Authorization'] = `Bearer ${getAccessToken()}`;
  }

  return request
    .post(API_URL + args.url)
    .send(args.params || undefined)
    .query(args.query || {})
    .set(finalHeaders || undefined)
    .responseType(args.responseType || '')
    .on('error', (error) => {
      tratarSessaoExpiradaFromError({ frontConfigs: args.frontConfigs, error });
      preHandleKnownErrors(error);
    })
    .on('response', (response) => {
      tratarSessaoExpirada({ frontConfigs: args.frontConfigs, response });
    });
};

export const put = (args: GenericRequest): RequestWrapper => {
  const finalHeaders = args.headers || {};

  if (!args.noAuth) {
    finalHeaders['Authorization'] = `Bearer ${getAccessToken()}`;
  }

  return request
    .put(API_URL + args.url)
    .query(args.query || {})
    .send(args.params || undefined)
    .set(finalHeaders || undefined)
    .responseType(args.responseType || '')
    .on('error', (error) => {
      tratarSessaoExpiradaFromError({ frontConfigs: args.frontConfigs, error });
      preHandleKnownErrors(error);
    })
    .on('response', (response) => {
      tratarSessaoExpirada({ frontConfigs: args.frontConfigs, response });
    });
};

export const patch = (args: GenericRequest): RequestWrapper => {
  const finalHeaders = args.headers || {};

  if (!args.noAuth) {
    finalHeaders['Authorization'] = `Bearer ${getAccessToken()}`;
  }

  return request
    .patch(API_URL + args.url)
    .send(args.params || undefined)
    .query(args.query || {})
    .set(finalHeaders || undefined)
    .responseType(args.responseType || '')
    .on('error', (error) => {
      tratarSessaoExpiradaFromError({ frontConfigs: args.frontConfigs, error });
      preHandleKnownErrors(error);
    })
    .on('response', (response) => {
      tratarSessaoExpirada({ frontConfigs: args.frontConfigs, response });
    });
};

export const del = (args: GenericRequest): RequestWrapper => {
  const finalHeaders = args.headers || {};

  if (!args.noAuth) {
    finalHeaders['Authorization'] = `Bearer ${getAccessToken()}`;
  }

  return request
    .delete(API_URL + args.url)
    .send(args.params || undefined)
    .query(args.query || {})
    .set(finalHeaders || undefined)
    .responseType(args.responseType || '')
    .on('error', (error) => {
      tratarSessaoExpiradaFromError({ frontConfigs: args.frontConfigs, error });
      preHandleKnownErrors(error);
    })
    .on('response', (response) => {
      tratarSessaoExpirada({ frontConfigs: args.frontConfigs, response });
    });
};

const deleteFirebaseToken = async (): Promise<boolean> => {
  let messaging;
  let response;
  let fireBaseApp;
  const allFirebaseApps = getApps();

  try {
    if (!allFirebaseApps.length) {
      return false;
    }

    fireBaseApp = getApp();
    messaging = getMessaging(fireBaseApp);
    response = await deleteToken(messaging);
    console.log('Token deleted.', response);
    return true;
  } catch (error: unknown) {
    console.log('Unable to delete token.', error);
    throw error;
  }
};

export const userLogout = async (args: {
  frontConfigs?: {
    navigateFunction?: (route: string) => void,
  },

  clientOptions?: {
    returnAll?: boolean,
  }
}): Promise<ResponseWrapper> => {
  const logoutEndpoint = '/auth/logout/';

  try {
    await deleteFirebaseToken();
  } catch (error: unknown) {
    console.log(error);
    throw error;
  }

  try {
    const response = await request
      .post(API_URL + logoutEndpoint)
      .set('Accept', 'application/json')
      .set('Authorization', `Bearer ${getAccessToken()}`)
      .type('application/json');

    if (args.clientOptions
      && 'returnAll' in args.clientOptions
      && args.clientOptions.returnAll === true
    ) {
      if (tratarSessaoExpirada({ frontConfigs: args.frontConfigs, response })) {
        throw new SessionExpiredException('Sessão expirada');
      }

      return response;
    }

    if (tratarSessaoExpirada({ frontConfigs: args.frontConfigs, response })) {
      throw new SessionExpiredException('Sessão expirada');
    }

    return response.body;
  } catch (error: unknown) {
    if (tratarSessaoExpiradaFromError({ frontConfigs: args.frontConfigs, error })) {
      throw new SessionExpiredException('Sessão expirada');
    }

    preHandleKnownErrors(error);
    throw error;
  }
};

export const downloadTermsAndConditions = async (args: {
  frontConfigs?: {
    navigateFunction?: (route: string) => void,
  },
}): Promise<ResponseWrapper> => {
  const endpoint = '/file/downloadTermsAndConditions';

  try {
    const response = await request
      .get(API_URL + endpoint)
      .responseType('blob');

    if (tratarSessaoExpirada({ frontConfigs: args.frontConfigs, response })) {
      throw new SessionExpiredException('Sessão expirada');
    }

    return response.body;
  } catch (error: unknown) {
    if (tratarSessaoExpiradaFromError({ frontConfigs: args.frontConfigs, error })) {
      throw new SessionExpiredException('Sessão expirada');
    }

    preHandleKnownErrors(error);
    throw error;
  }
};
